import { useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { api, ifMatch, newIdempotencyKey, unwrap } from '@/api/client';
import { useCommand } from '@/app/commands';
import { orgKey } from '@/app/queryClient';
import { useOrg } from '@/app/session';
import { Banner, Button, Checkbox, EmptyState, Field, Menu, Modal, Section, StatusDot, TextInput, Time, useToast } from '@/components/ui';
import { DataTable, FilterChip } from '@/components/data';
import { ConfirmDialog, FormErrorSummary, ScopeList, fieldErrors, usePagedList, type Schemas } from '@/features/settings/shared';
import '@/features/settings/settings.css';

type ApiKey = Schemas['ApiKey'];
type Scope = Schemas['Scope'];

function toDateInput(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function ApiKeysPage() {
  const { orgId, orgName, isAdmin } = useOrg();
  const [all, setAll] = useState(false);
  const owner: Schemas['ApiKeyOwnerFilter'] = isAdmin && all ? 'all' : 'self';
  const listKey = orgKey(orgId, 'api-keys');
  const { pager, query } = usePagedList<ApiKey>([...listKey, owner], (q, signal) =>
    unwrap(api.GET('/api/v1/orgs/{orgId}/api-keys', { params: { path: { orgId }, query: { ...q, owner } }, signal })),
  );
  const [mintOpen, setMintOpen] = useState(false);
  const [issued, setIssued] = useState<Schemas['ApiKeyIssued'] | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);
  const [rotateTarget, setRotateTarget] = useState<ApiKey | null>(null);

  const revoke = useCommand({
    run: (k: ApiKey) =>
      unwrap(
        api.DELETE('/api/v1/orgs/{orgId}/api-keys/{keyId}', {
          params: { path: { orgId, keyId: k.id }, header: { 'If-Match': ifMatch(k.version), 'Idempotency-Key': newIdempotencyKey() } },
        }),
      ),
    success: 'Key revoked. Requests with the key are refused.',
    invalidate: [listKey],
    onSuccess: () => setRevokeTarget(null),
  });
  const rotate = useCommand({
    run: (k: ApiKey) =>
      unwrap(
        api.POST('/api/v1/orgs/{orgId}/api-keys/{keyId}/rotate', {
          params: { path: { orgId, keyId: k.id }, header: { 'If-Match': ifMatch(k.version), 'Idempotency-Key': newIdempotencyKey() } },
          body: { old_key_expires_at: null },
        }),
      ),
    invalidate: [listKey],
    onSuccess: (r) => {
      setRotateTarget(null);
      setIssued(r);
    },
  });

  const columns: ColumnDef<ApiKey, unknown>[] = [
    { id: 'label', header: 'Label', cell: ({ row }) => row.original.label, meta: { pinned: true } },
    { id: 'prefix', header: 'Prefix', cell: ({ row }) => <span className="mono">{row.original.prefix}</span> },
    { id: 'state', header: 'State', cell: ({ row }) => <StatusDot domain="apiKey" value={row.original.state} /> },
    {
      id: 'actor',
      header: 'Bound to',
      cell: ({ row }) => (
        <span title={`Account ${row.original.actor_id}`}>
          <StatusDot domain="actor" value={row.original.actor_kind} />
        </span>
      ),
    },
    { id: 'scopes', header: 'Scopes', cell: ({ row }) => <ScopeList scopes={row.original.scopes} /> },
    { id: 'created', header: 'Created', cell: ({ row }) => <Time value={row.original.created_at} /> },
    { id: 'expires', header: 'Expires', cell: ({ row }) => <Time value={row.original.expires_at} /> },
    { id: 'used', header: 'Last used', cell: ({ row }) => <Time value={row.original.last_used_at} emptyLabel="Never used" /> },
    {
      id: 'actions',
      header: '',
      meta: { width: 56 },
      cell: ({ row }) =>
        row.original.state === 'active' ? (
          <Menu
            label={`Actions for key ${row.original.label}`}
            iconOnly
            icon="more"
            size="sm"
            align="right"
            items={[
              { label: 'Rotate', icon: 'refresh', onSelect: () => setRotateTarget(row.original) },
              { label: 'Revoke', icon: 'x', danger: true, onSelect: () => setRevokeTarget(row.original) },
            ]}
          />
        ) : null,
    },
  ];

  return (
    <Section
      title="API keys"
      actions={
        <Button variant="primary" icon="plus" onClick={() => setMintOpen(true)}>
          Create key
        </Button>
      }
    >
      <p className="small muted">Scoped keys for {orgName}. A key never holds more than you can delegate. The secret is shown once when you create one.</p>
      <DataTable
        caption="API keys"
        columns={columns}
        getRowId={(k) => k.id}
        query={query}
        pager={pager}
        sort={[]}
        onSortChange={() => undefined}
        toolbar={<>{isAdmin && <FilterChip label="All keys in organization" on={all} onToggle={() => setAll((v) => !v)} />}</>}
        storageKey="settings.api-keys"
        defaultHidden={['prefix', 'created', 'expires']}
        empty={<EmptyState title="No API keys yet." />}
      />
      {mintOpen && <MintDialog onClose={() => setMintOpen(false)} onIssued={(r) => { setMintOpen(false); setIssued(r); }} />}
      {issued && <SecretReveal issued={issued} onClose={() => setIssued(null)} />}
      {revokeTarget && (
        <ConfirmDialog
          open
          reason="none"
          danger
          title="Revoke key"
          confirmLabel="Revoke key"
          onClose={() => setRevokeTarget(null)}
          pending={revoke.isPending}
          onConfirm={() => revoke.mutate(revokeTarget)}
          effects={[
            `Key ${revokeTarget.label} (${revokeTarget.prefix}) stops working immediately.`,
            'Work in progress using the key is released and requeued or failed.',
            'Mail syncs using the key stop syncing until given a new key. Imported evidence stays.',
            'Mail already submitted is not recalled.',
          ]}
        />
      )}
      {rotateTarget && (
        <ConfirmDialog
          open
          reason="none"
          title="Rotate key"
          confirmLabel="Rotate key"
          onClose={() => setRotateTarget(null)}
          pending={rotate.isPending}
          onConfirm={() => rotate.mutate(rotateTarget)}
          effects={[
            `A new secret is issued for ${rotateTarget.label} with the same scopes.`,
            'The old secret is retired according to server policy.',
            'The new secret is shown once.',
          ]}
        />
      )}
    </Section>
  );
}

function MintDialog({ onClose, onIssued }: { onClose: () => void; onIssued: (r: Schemas['ApiKeyIssued']) => void }) {
  const { orgId, scopes: delegable } = useOrg();
  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState<Set<Scope>>(new Set());
  const [expires, setExpires] = useState(() => toDateInput(new Date(Date.now() + 90 * 86400_000)));
  const mint = useCommand({
    run: () =>
      unwrap(
        api.POST('/api/v1/orgs/{orgId}/api-keys', {
          params: { path: { orgId }, header: { 'Idempotency-Key': newIdempotencyKey() } },
          body: { label: label.trim(), scopes: [...scopes], expires_at: new Date(`${expires}T23:59:59Z`).toISOString() },
        }),
      ),
    invalidate: [orgKey(orgId, 'api-keys')],
    onSuccess: onIssued,
  });
  const errs = fieldErrors(mint.error);
  const toggle = (s: Scope) =>
    setScopes((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  return (
    <Modal
      open
      onClose={onClose}
      title="Create API key"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={mint.isPending} disabled={!label.trim() || scopes.size === 0} onClick={() => mint.mutate(undefined)}>
            Create key
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="Label" required error={errs.label} hint="For example: laptop mail ingest.">
          <TextInput value={label} maxLength={120} onChange={(e) => setLabel(e.target.value)} />
        </Field>
        <Field label="Expires on" required error={errs.expires_at}>
          <TextInput type="date" value={expires} min={toDateInput(new Date())} onChange={(e) => setExpires(e.target.value)} />
        </Field>
        <fieldset className="stack-sm">
          <legend className="field-label">Scopes you can delegate</legend>
          <p className="xsmall muted">Use separate keys for mail ingest and outbound transport. No wildcard scope exists.</p>
          {delegable.length === 0 ? (
            <Banner tone="accent">Your membership has no scopes you can delegate.</Banner>
          ) : (
            <div className="scope-grid">
              {delegable.map((s) => (
                <Checkbox key={s} label={<span className="mono small">{s}</span>} checked={scopes.has(s as Scope)} onChange={() => toggle(s as Scope)} />
              ))}
            </div>
          )}
          {errs.scopes && <p className="field-error">{errs.scopes}</p>}
        </fieldset>
        <FormErrorSummary error={mint.error} />
      </div>
    </Modal>
  );
}

/** Secret shown once. Never logged, never stored; copying is user initiated. */
function SecretReveal({ issued, onClose }: { issued: Schemas['ApiKeyIssued']; onClose: () => void }) {
  const toast = useToast();
  const [stored, setStored] = useState(false);
  const copy = async () => {
    if (!issued.secret) return;
    try {
      await navigator.clipboard.writeText(issued.secret);
      toast.show({ tone: 'neutral', title: 'Copied to clipboard' });
    } catch {
      toast.show({ tone: 'accent', title: 'Copy failed', body: 'Select the secret and copy manually.' });
    }
  };
  return (
    <Modal
      open
      dismissible={false}
      onClose={onClose}
      title="Store the secret now"
      description="Shown once. If lost, rotate or revoke the key."
      footer={
        <Button variant="primary" disabled={!stored} onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="stack">
        <p className="small">
          Key <strong>{issued.api_key.label}</strong>, prefix <span className="mono">{issued.api_key.prefix}</span>
        </p>
        {issued.secret ? (
          <>
            <div className="secret-reveal" aria-label="API key secret">
              {issued.secret}
            </div>
            <div>
              <Button icon="file" onClick={() => void copy()}>
                Copy
              </Button>
            </div>
          </>
        ) : (
          <Banner tone="accent">No secret returned by the server.</Banner>
        )}
        {issued.previous_api_key && (
          <p className="small muted">
            Previous key {issued.previous_api_key.prefix} expires <Time value={issued.previous_api_key.expires_at} />.
          </p>
        )}
        <Checkbox label="Secret stored safely" checked={stored} onChange={(e) => setStored(e.target.checked)} />
      </div>
    </Modal>
  );
}
